/**
 * PlatformAdminGuard — гейт доступа ко ВСЕМ экранам admin-app.
 *
 * admin-app предназначен исключительно для роли platform_admin. Любой другой
 * аутентифицированный пользователь видит экран «Доступ ограничен», а
 * неаутентифицированный — редиректится на /login (см. RequireAuth).
 *
 * JwtClaims в @hsc/types не объявляет `roles` (расширяемый payload —
 * см. login flow). Читаем claim как опциональный string[].
 */
import type { ReactNode } from 'react';
import { useAuth } from '@hsc/auth';

export const PLATFORM_ADMIN_ROLE = 'platform_admin' as const;

export const PLATFORM_ADMIN_DENIED_MESSAGE =
  'Доступ только для администратора платформы';

export interface PlatformAdminGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

/** Возвращает true только при явном членстве в роли platform_admin. */
export function isPlatformAdmin(claims: unknown): boolean {
  if (!claims) return false;
  const raw = (claims as { roles?: unknown }).roles;
  if (!Array.isArray(raw)) return false;
  return raw.includes(PLATFORM_ADMIN_ROLE);
}

/**
 * Рендерит children только когда текущий JWT несёт роль platform_admin.
 * Иначе — локализованное уведомление об отказе.
 */
export function PlatformAdminGuard({
  children,
  fallback,
}: PlatformAdminGuardProps): JSX.Element {
  const { claims } = useAuth();
  if (isPlatformAdmin(claims)) {
    return <>{children}</>;
  }
  return (
    <>
      {fallback ?? (
        <main className="hsc-page" data-testid="platform-admin-denied">
          <h1 className="hsc-page__title">Доступ ограничен</h1>
          <p>{PLATFORM_ADMIN_DENIED_MESSAGE}</p>
        </main>
      )}
    </>
  );
}
